import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { endOfMonth, format, startOfMonth } from "date-fns";
import { obterDashboardFinanceiro } from "../services/financeiro-dashboard.service";
import { mapDashboardApiToView } from "../helpers/map-dashboard-api-to-view";
import { writeFinanceiroDashboardPeriodo } from "../../financeiro-periodo-storage";
import type {
  DashboardFinanceiroApiResponse,
  DashboardFinanceiroFiltros,
  DashboardFinanceiroViewModel,
  GranularidadeFinanceira,
} from "../types/financeiro-dashboard.types";

function formatYmd(d: Date): string {
  return format(d, "yyyy-MM-dd");
}

function filtrosIniciaisMesAtual(): DashboardFinanceiroFiltros {
  const now = new Date();
  return {
    dataInicio: formatYmd(startOfMonth(now)),
    dataFim: formatYmd(endOfMonth(now)),
    granularidade: "mensal",
    tipoDataInscricoes: "pagamento",
  };
}

export type UseDashboardFinanceiroResult = {
  raw: DashboardFinanceiroApiResponse | null;
  data: DashboardFinanceiroViewModel | null;
  loading: boolean;
  error: string | null;
  filtros: DashboardFinanceiroFiltros;
  /** Filtros efetivamente usados na última requisição (alinha com DRE e demais widgets). */
  filtrosAplicados: DashboardFinanceiroFiltros;
  setFiltros: Dispatch<SetStateAction<DashboardFinanceiroFiltros>>;
  aplicarFiltros: (override?: DashboardFinanceiroFiltros) => void;
  setGranularidade: (g: GranularidadeFinanceira) => void;
  setTipoDataInscricoes: (
    t: DashboardFinanceiroFiltros["tipoDataInscricoes"]
  ) => void;
  refetch: () => void;
  isEmpty: boolean;
};

export function useDashboardFinanceiro(): UseDashboardFinanceiroResult {
  const [filtros, setFiltros] = useState<DashboardFinanceiroFiltros>(
    filtrosIniciaisMesAtual
  );
  const [applied, setApplied] = useState<DashboardFinanceiroFiltros>(
    filtrosIniciaisMesAtual
  );
  const [raw, setRaw] = useState<DashboardFinanceiroApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const paramsQuery = useMemo(
    () => ({
      dataInicio: applied.dataInicio,
      dataFim: applied.dataFim,
      granularidade: applied.granularidade,
      tipoDataInscricoes: applied.tipoDataInscricoes ?? "pagamento",
    }),
    [applied]
  );

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, status } = await obterDashboardFinanceiro(paramsQuery);
        if (cancelled) return;
        if (status === 403) {
          setError("Sem permissão para o dashboard financeiro.");
          setRaw(null);
          return;
        }
        if (status !== 200 || !data) {
          setError("Não foi possível carregar o dashboard financeiro.");
          setRaw(null);
          return;
        }
        setRaw(data);
      } catch (e: unknown) {
        if (cancelled) return;
        const msg =
          e instanceof Error ? e.message : "Falha ao carregar o dashboard.";
        setError(msg);
        setRaw(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [paramsQuery, tick]);

  useEffect(() => {
    if (applied.dataInicio?.trim() && applied.dataFim?.trim()) {
      writeFinanceiroDashboardPeriodo({
        dataInicio: applied.dataInicio.trim(),
        dataFim: applied.dataFim.trim(),
      });
    }
  }, [applied.dataInicio, applied.dataFim]);

  const data = useMemo(() => (raw ? mapDashboardApiToView(raw) : null), [raw]);

  const isEmpty = useMemo(() => {
    if (!data) return false;
    const lojaVazia =
      data.vendas.quantidadePedidos === 0 && data.vendas.faturamentoBruto === 0;
    const inscricoesVazias =
      data.inscricoes.quantidadeInscricoes === 0 &&
      data.inscricoes.faturamentoBruto === 0;
    return lojaVazia && inscricoesVazias;
  }, [data]);

  const refetch = useCallback(() => {
    setTick((n) => n + 1);
  }, []);

  const aplicarFiltros = useCallback(
    (override?: DashboardFinanceiroFiltros) => {
      const next = override ?? filtros;
      setApplied(next);
      if (override) {
        setFiltros(next);
      }
      if (next.dataInicio?.trim() && next.dataFim?.trim()) {
        writeFinanceiroDashboardPeriodo({
          dataInicio: next.dataInicio.trim(),
          dataFim: next.dataFim.trim(),
        });
      }
    },
    [filtros]
  );

  const setGranularidade = useCallback((g: GranularidadeFinanceira) => {
    setFiltros((prev) => ({ ...prev, granularidade: g }));
  }, []);

  const setTipoDataInscricoes = useCallback(
    (tipoDataInscricoes: DashboardFinanceiroFiltros["tipoDataInscricoes"]) => {
      const next = {
        ...applied,
        tipoDataInscricoes: tipoDataInscricoes ?? "pagamento",
      };
      setFiltros((prev) => ({
        ...prev,
        tipoDataInscricoes: next.tipoDataInscricoes,
      }));
      setApplied(next);
    },
    [applied]
  );

  return {
    raw,
    data,
    loading,
    error,
    filtros,
    filtrosAplicados: applied,
    setFiltros,
    aplicarFiltros,
    setGranularidade,
    setTipoDataInscricoes,
    refetch,
    isEmpty,
  };
}
